import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./SupportMessagePage.css";

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

const loadTicket = (stateTicket, ticketId) => {
  if (stateTicket) return stateTicket;
  if (!ticketId) return null;
  try {
    const raw = localStorage.getItem(`supportTicket_${ticketId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const SupportMessagePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ticketId } = useParams();
  const ticket = useMemo(
    () => loadTicket(location.state?.ticket, ticketId),
    [location.state?.ticket, ticketId]
  );

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [messages, setMessages] = useState(() => {
    const list = Array.isArray(ticket?.ticket)
      ? ticket.ticket.map((m) => ({
        from: m?.replyBy === 1 ? "user" : "support",
        text: m?.query || "",
        sentAt: m?.createdAt || m?.updatedAt || new Date().toISOString(),
      }))
      : [];
    return list;
  });

  if (!ticket) {
    return (
      <main className="dashboard-main dashboard-subpage support-message-page">
        <div className="dashboard-body">
          <button type="button" className="support-message-back" onClick={() => navigate("/dashboard/support")}>
            <i className="fas fa-arrow-left" /> Support Message
          </button>
          <p>No ticket data. Go back to Support list.</p>
        </div>
      </main>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError("");
    const msg = message.trim();
    try {
      const res = await AdminService.replySupportTicket(ticket._id, msg, 0);
      if (res?.success) {
        setMessages((prev) => [...prev, { from: "support", text: msg, sentAt: new Date().toISOString() }]);
        setMessage("");
      } else {
        setError(res?.message || "Failed to send reply.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  const updateStatusAndGoBack = async (status) => {
    setUpdatingStatus(true);
    setError("");
    try {
      const res = await AdminService.updateSupportTicketStatus(ticket._id, status);
      if (res?.success) {
        navigate("/dashboard/support", { state: { updatedTicket: { _id: ticket._id, status } } });
      } else {
        setError(res?.message || `Failed to mark ticket as ${status}.`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Failed to mark ticket as ${status}.`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage support-message-page">
      <div className="dashboard-body">
        <button type="button" className="support-message-back" onClick={() => navigate("/dashboard/support")}>
          <i className="fas fa-arrow-left" /> Support Message
        </button>

        <div className="support-message-layout">
          <section className="support-message-description-card">
            <h3 className="support-message-card-title">Description</h3>
            <p className="support-message-description-text">{ticket.description || "No description."}</p>
          </section>

          <section className="support-message-chat-card">
            <div className="support-message-chat-header">
              <div className="support-message-chat-user">
                <span className="support-message-chat-email">Email : {ticket?.userData?.emailId || "-----"}</span>
                <span className="support-message-chat-userid">User Id : {ticket?.userData?.uuid || "-----"}</span>
              </div>
              <div className="support-message-chat-actions">
                {(ticket.status || "").toLowerCase() === "open" && (
                  <>
                    <button type="button" className="support-message-btn support-message-btn--close" onClick={() => updateStatusAndGoBack("Closed")} disabled={updatingStatus}>
                      Close
                    </button>
                    <button type="button" className="support-message-btn support-message-btn--resolve" onClick={() => updateStatusAndGoBack("Resolved")} disabled={updatingStatus}>
                      Resolve
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="support-message-chat-body">
              {messages.length === 0 ? (
                <p className="support-message-chat-empty">No messages yet. Start the conversation below.</p>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`support-message-bubble-wrap support-message-bubble-wrap--${msg.from}`}>
                    <div className={`support-message-bubble support-message-bubble--${msg.from}`}>
                      <span className="support-message-bubble-label">{msg.from === "user" ? "User" : "Support Team"}</span>
                      <p className="support-message-bubble-text">{msg.text}</p>
                      <span className="support-message-bubble-time">{formatTime(msg.sentAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form className="support-message-compose" onSubmit={handleSend}>
              <div className="support-message-toolbar">
                <select className="support-message-toolbar-select" aria-label="Text style">
                  <option>Normal</option>
                </select>
                <button type="button" className="support-message-toolbar-btn" title="Bold"><i className="fas fa-bold" /></button>
                <button type="button" className="support-message-toolbar-btn" title="Italic"><i className="fas fa-italic" /></button>
                <button type="button" className="support-message-toolbar-btn" title="Underline"><i className="fas fa-underline" /></button>
                <button type="button" className="support-message-toolbar-btn" title="Strikethrough"><i className="fas fa-strikethrough" /></button>
                <button type="button" className="support-message-toolbar-btn" title="Link"><i className="fas fa-link" /></button>
                <button type="button" className="support-message-toolbar-btn" title="Text color"><i className="fas fa-font" /></button>
              </div>
              <textarea
                className="support-message-textarea"
                placeholder="Write your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
              <div className="support-message-send-wrap">
                <button type="submit" className="support-message-btn-send" disabled={!message.trim() || sending}>
                  {sending ? "Sending..." : "Send"}
                </button>
              </div>
              {error && <p className="support-message-chat-empty" style={{ padding: "0.5rem 0 0", textAlign: "left" }}>{error}</p>}
            </form>
          </section>
        </div>
      </div>
    </main>
  );
};

export default SupportMessagePage;
